// System health — aggregates already-existing operational signals that
// the admin /admin/system page surfaces. No new tables, no new RPCs.
//
// Every panel here is read-only and admin-scoped (RLS already permits
// admin SELECT on these tables; non-admin profiles will simply see empty
// rows or zeros, which is fine — the route is admin-only at the router).

import { supabase, isSupabaseConfigured } from "./supabase";
import { ROLES } from "./roles";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

/**
 * Notification log breakdown over the last `days` days. Drives the
 * "Email channel" panel — sent vs failed counts, plus the most recent
 * failure reason (so admins can see at a glance whether RESEND is
 * actually delivering).
 */
export async function loadNotificationsHealth({ days = 7 } = {}) {
  ensureLive();
  const sinceIso = isoDaysAgo(days);
  const { data, error } = await supabase
    .from("notifications_log")
    .select("kind, status, error, created_at")
    .gte("created_at", sinceIso)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;

  const rows = data || [];
  const byKind = {};
  const byStatus = { sent: 0, failed: 0, queued: 0 };
  let lastFailure = null;
  for (const r of rows) {
    byStatus[r.status] = (byStatus[r.status] || 0) + 1;
    if (!byKind[r.kind]) byKind[r.kind] = { sent: 0, failed: 0, queued: 0, total: 0 };
    byKind[r.kind][r.status] = (byKind[r.kind][r.status] || 0) + 1;
    byKind[r.kind].total += 1;
    if (r.status === "failed" && !lastFailure) {
      lastFailure = { kind: r.kind, error: r.error, at: r.created_at };
    }
  }
  return {
    days,
    total: rows.length,
    by_status: byStatus,
    by_kind: byKind,
    last_failure: lastFailure,
  };
}

/**
 * Push opt-in coverage by role. Counts unique subscribed profiles per
 * role using role_assignments (the canonical role source) joined to
 * push_subscriptions. Admin RLS already scopes both tables to the
 * caller's org, so we never accidentally count cross-org subscribers.
 *
 * Subscriptions are unique per (profile_id, endpoint). We dedupe by
 * profile_id so a parent with two devices counts once.
 */
export async function loadPushCoverage() {
  ensureLive();

  const [{ data: subs, error: subErr }, { data: roleRows, error: rErr }] =
    await Promise.all([
      supabase.from("push_subscriptions").select("profile_id"),
      supabase.from("role_assignments").select("profile_id, role"),
    ]);
  if (subErr) throw subErr;
  if (rErr)   throw rErr;

  const subscribed = new Set((subs || []).map((s) => s.profile_id));
  // A profile may have multiple role_assignments in theory; we still
  // dedupe per role.
  const profilesByRole = {
    [ROLES.PARENT]: new Set(),
    [ROLES.TEACHER]: new Set(),
    [ROLES.ADMIN]: new Set(),
  };
  for (const r of roleRows || []) {
    if (profilesByRole[r.role]) profilesByRole[r.role].add(r.profile_id);
  }

  const out = {};
  for (const role of [ROLES.PARENT, ROLES.TEACHER, ROLES.ADMIN]) {
    const ids = profilesByRole[role];
    const total = ids.size;
    let optedIn = 0;
    ids.forEach((id) => { if (subscribed.has(id)) optedIn += 1; });
    out[role] = {
      total,
      opted_in: optedIn,
      pct: total === 0 ? 0 : Math.round((optedIn / total) * 100),
    };
  }
  return out;
}

/**
 * CMS publish state — counts of published vs draft pages and
 * announcements. Useful for admins to know whether the public site is
 * live and current.
 *
 * `cms.pages` uses a `status` enum ('draft','published','archived').
 * `cms.announcements` uses `published_at IS NULL` to mean draft and
 * NOT NULL to mean published — different model, same dashboard.
 */
export async function loadPublicSiteHealth() {
  ensureLive();

  const pagesQ = supabase
    .schema("cms")
    .from("pages")
    .select("status");
  const annQ = supabase
    .schema("cms")
    .from("announcements")
    .select("published_at");

  const [pagesRes, annRes] = await Promise.all([pagesQ, annQ]);
  const pages = pagesRes.data || [];
  const ann = annRes.data || [];

  return {
    pages: {
      total: pages.length,
      published: pages.filter((r) => r.status === "published").length,
      draft: pages.filter((r) => r.status === "draft").length,
      archived: pages.filter((r) => r.status === "archived").length,
    },
    announcements: {
      total: ann.length,
      published: ann.filter((r) => r.published_at).length,
      draft: ann.filter((r) => !r.published_at).length,
      archived: 0,
    },
  };
}

/**
 * Counts of open enrollment inquiries, by status. Helps an admin see
 * whether the public funnel is generating responses and they're keeping
 * up with the inbox.
 */
export async function loadInquiryHealth() {
  ensureLive();
  const { data, error } = await supabase
    .from("enrollment_inquiries")
    .select("status, created_at")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  const rows = data || [];
  const byStatus = {};
  for (const r of rows) byStatus[r.status] = (byStatus[r.status] || 0) + 1;

  // Oldest open inquiry — surfaces inbox neglect.
  const open = rows.filter((r) => r.status === "open" || r.status === "new");
  let oldestOpenAt = null;
  if (open.length) {
    oldestOpenAt = open[open.length - 1].created_at;
  }
  return {
    total: rows.length,
    by_status: byStatus,
    oldest_open_at: oldestOpenAt,
    open_count: open.length,
  };
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

export function formatRelativeAge(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const hours = Math.floor(ms / 36e5);
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours} h ago`;
    const days = Math.floor(hours / 24);
    return `${days} d ago`;
  } catch {
    return iso;
  }
}
